/**
 * Frontend free-time service.
 *
 * Free time = idle wait after previous stop is done:
 * - Normal trip: approx_pickup − (prev_dropoff + unload) − eta_to_pickup
 * - Break:       break_start − (prev_dropoff + unload)   (no travel ETA)
 * - Multiload group: only P1 (first pickup); previous = last trip *outside* the group
 * - If previous is itself a multiload group: ready = last *dropoff* in that group's route
 *   (e.g. P1→P2→D2→D1 → use D1 + unload), not an arbitrary card in the group
 *
 * Skips:
 * - busy / in-progress trips (onway, arrived, started, …)
 * - multiload members after P1 (passenger already onboard)
 * - first item in bucket (no previous trip)
 * - first trip of a new fleet window (fleet mapping only; previous vehicle
 *   already returned to garage). Driver-mode free time is unchanged.
 */

import { sortTripsForDisplay } from './tripSections';

const BUSY_TRIP_STATUSES = new Set([
  'onway',
  'on way',
  'arrived',
  'started',
  'pickedup',
  'picked up',
  'inprogress',
  'in progress',
  'in_progress',
]);

/**
 * @param {string|Date|null|undefined} value
 * @returns {number|null} epoch ms
 */
function toMillis(value) {
  if (value == null || value === '') return null;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function tripKeySet(trip) {
  const keys = [];
  const a = trip?.trip_id ?? trip?.id;
  const b = trip?.id;
  if (a != null && a !== '') keys.push(String(a));
  if (b != null && b !== '' && String(b) !== String(a)) keys.push(String(b));
  return keys;
}

function findTripById(trips, tripId) {
  const want = String(tripId || '').trim();
  if (!want || !Array.isArray(trips)) return null;
  return trips.find((t) => tripKeySet(t).some((k) => k === want)) || null;
}

/**
 * Busy / in-progress trip — no free-time wait (driver already working it).
 * @param {object} trip
 * @returns {boolean}
 */
export function isBusyOrInProgressTrip(trip) {
  if (!trip) return false;
  if (trip.is_busy_trip === true) return true;
  if (trip.sequence === 0) return true;
  const status = String(trip.trip_status || trip.status || '')
    .trim()
    .toLowerCase();
  return status !== '' && BUSY_TRIP_STATUSES.has(status);
}

/**
 * Multiload: only the first pickup (P1) gets free time for the group.
 * Later pickups already have a member onboard — skip.
 * @param {object} trip
 * @returns {boolean} true if this trip should be skipped for free time
 */
export function shouldSkipMultiloadFreeTime(trip) {
  if (!trip || trip.is_multiload !== true) return false;

  if (trip.is_multiload_primary === true) return false;
  if (trip.is_multiload_primary === false) return true;

  const order = Number(trip.multiload_pickup_order);
  if (Number.isFinite(order) && order > 0) {
    return order !== 1;
  }

  // Fallback: partner list — lowest trip_id treated as first if no order
  const partners = trip.multiload_partner_ids;
  if (Array.isArray(partners) && partners.length > 0) {
    const selfId = trip.trip_id ?? trip.id;
    const earlier = partners.filter((id) => id < selfId);
    return earlier.length > 0;
  }

  return false;
}

/**
 * P1 trip for a multiload section (for group-level free-time banner).
 * @param {{ trips?: object[] }|object[]|null|undefined} sectionOrTrips
 * @returns {object|null}
 */
export function getMultiloadGroupP1Trip(sectionOrTrips) {
  const trips = Array.isArray(sectionOrTrips)
    ? sectionOrTrips
    : sectionOrTrips?.trips || [];
  if (!trips.length) return null;
  const byOrder = trips.find((t) => Number(t.multiload_pickup_order) === 1);
  if (byOrder) return byOrder;
  const primary = trips.find((t) => t.is_multiload_primary === true);
  if (primary) return primary;
  return trips[0];
}

/**
 * Last dropoff trip in a multiload group's saved route (e.g. …→D3→D2→D1 → D1).
 * Falls back to member with latest approx dropoff if route missing.
 * @param {object[]|null|undefined} driverTrips
 * @param {string|null|undefined} groupId
 * @param {object|null|undefined} multiloadRoutes
 * @returns {object|null}
 */
export function getMultiloadGroupLastDropoffTrip(
  driverTrips,
  groupId,
  multiloadRoutes = null
) {
  const gid = groupId != null ? String(groupId).trim() : '';
  if (!gid || !Array.isArray(driverTrips)) return null;

  const members = driverTrips.filter(
    (t) =>
      t?.is_multiload &&
      t.multiload_group_id != null &&
      String(t.multiload_group_id) === gid
  );
  if (!members.length) return null;

  const route = multiloadRoutes?.[gid] || multiloadRoutes?.[String(gid)];
  if (Array.isArray(route) && route.length > 0) {
    for (let i = route.length - 1; i >= 0; i -= 1) {
      const step = route[i];
      const stop = String(step?.stop_type || step?.stopType || '').toLowerCase();
      if (stop !== 'dropoff' && stop !== 'd') continue;
      const tid = step?.trip_id ?? step?.id;
      const hit = findTripById(members, tid) || findTripById(driverTrips, tid);
      if (hit) return hit;
    }
  }

  // No route: pick member with latest approx dropoff
  let best = null;
  let bestMs = -Infinity;
  for (const m of members) {
    const ms = toMillis(
      m.trip_details?.approximate_dropoff_time ||
        m.approximate_dropoff_time ||
        m.appointment_time
    );
    if (ms != null && ms >= bestMs) {
      bestMs = ms;
      best = m;
    }
  }
  return best || members[members.length - 1];
}

/**
 * When previous trip is in a multiload group, use that group's last route dropoff.
 * @param {object|null|undefined} previousTrip
 * @param {object[]|null|undefined} driverTrips
 * @param {object|null|undefined} multiloadRoutes
 * @returns {object|null}
 */
export function resolvePreviousReadyTrip(
  previousTrip,
  driverTrips = null,
  multiloadRoutes = null
) {
  if (!previousTrip) return null;
  if (previousTrip.is_multiload && previousTrip.multiload_group_id != null) {
    const lastDrop = getMultiloadGroupLastDropoffTrip(
      driverTrips,
      previousTrip.multiload_group_id,
      multiloadRoutes
    );
    if (lastDrop) return lastDrop;
  }
  return previousTrip;
}

/**
 * When the driver is ready to leave this trip for the next one (epoch ms).
 * - Break: break_end
 * - Busy: available_at (already post-work)
 * - Else: approximate_dropoff + unload minutes
 * @param {object|null|undefined} trip
 * @returns {number|null}
 */
export function getTripReadyForNextMs(trip) {
  if (!trip) return null;

  if (trip.is_break) {
    return toMillis(
      trip.break_end ||
        trip.appointment_time ||
        trip.trip_details?.approximate_dropoff_time
    );
  }

  if (isBusyOrInProgressTrip(trip) && trip.available_at) {
    return toMillis(trip.available_at);
  }

  const dropoffMs = toMillis(
    trip.trip_details?.approximate_dropoff_time ||
      trip.approximate_dropoff_time ||
      trip.available_at ||
      trip.appointment_time
  );
  if (dropoffMs == null) return null;

  const unloadMin =
    Number(
      trip.unloadTime ??
        trip.unload_time ??
        trip.trip_details?.unload_time_minutes ??
        0
    ) || 0;

  return dropoffMs + unloadMin * 60000;
}

function windowIndexOrNull(trip) {
  const idx = trip?.fleet_window_index;
  if (idx == null || idx === '') return null;
  const n = Number(idx);
  return Number.isFinite(n) ? n : null;
}

/**
 * Fleet mapping only. Skip free time when both trips are tagged to different
 * windows (garage handoff). Missing index = driver mode, keep the old formula.
 */
export function tripsShareFleetWindow(trip, previousTrip) {
  const a = windowIndexOrNull(trip);
  const b = windowIndexOrNull(previousTrip);
  if (a == null || b == null) return true;
  return a === b;
}

/**
 * Previous trip for free-time: display order, skipping other members of the
 * same multiload group (P1 waits after the trip *before* the group, not P2).
 * @param {object[]|null|undefined} trips
 * @param {object} currentTrip
 * @returns {object|null}
 */
export function findPreviousTripInBucket(trips, currentTrip) {
  if (!Array.isArray(trips) || !currentTrip) return null;
  const sorted = sortTripsForDisplay(trips);
  const selfId = String(currentTrip.trip_id ?? currentTrip.id ?? '');
  let idx = sorted.findIndex(
    (t) => String(t.trip_id ?? t.id ?? '') === selfId && selfId !== ''
  );
  if (idx < 0) {
    idx = trips.indexOf(currentTrip);
    if (idx >= 0) {
      const ref = trips[idx];
      idx = sorted.indexOf(ref);
    }
  }
  if (idx <= 0) return null;

  const gid =
    currentTrip.is_multiload && currentTrip.multiload_group_id != null
      ? String(currentTrip.multiload_group_id)
      : null;

  for (let j = idx - 1; j >= 0; j -= 1) {
    const prev = sorted[j];
    if (
      gid &&
      prev?.is_multiload &&
      prev.multiload_group_id != null &&
      String(prev.multiload_group_id) === gid
    ) {
      continue;
    }
    return prev;
  }
  return null;
}

/**
 * Target start time for free-time gap (approx pickup, or break start).
 * @param {object} trip
 * @returns {{ ms: number, raw: string|null }|null}
 */
function getTargetStart(trip) {
  if (trip?.is_break) {
    const raw =
      trip.break_start ||
      trip.schedule_time ||
      trip.trip_details?.break_start ||
      null;
    const ms = toMillis(raw);
    if (ms == null) return null;
    return { ms, raw };
  }
  const raw =
    trip.trip_details?.approximate_pickup_time ??
    trip.approximate_pickup_time ??
    null;
  const ms = toMillis(raw);
  if (ms == null) return null;
  return { ms, raw };
}

/**
 * @param {object|null|undefined} trip
 * @param {object|null|undefined} previousTrip
 * @param {object[]|null|undefined} driverTrips
 * @param {object|null|undefined} multiloadRoutes
 */
export function calculateTripFreeTime(
  trip,
  previousTrip = null,
  driverTrips = null,
  multiloadRoutes = null
) {
  if (!trip) return null;
  if (isBusyOrInProgressTrip(trip)) return null;
  if (shouldSkipMultiloadFreeTime(trip)) return null;
  if (!previousTrip) return null;
  if (!tripsShareFleetWindow(trip, previousTrip)) return null;

  const target = getTargetStart(trip);
  if (!target) return null;

  const readyTrip = resolvePreviousReadyTrip(
    previousTrip,
    driverTrips,
    multiloadRoutes
  );
  const prevReadyMs = getTripReadyForNextMs(readyTrip);
  if (prevReadyMs == null) return null;

  const etaMinutes = trip.is_break
    ? 0
    : Number(trip.eta_to_pickup_minutes ?? trip.etaToPickupMinutes ?? 0) || 0;

  const freeMinutes = Math.round((target.ms - prevReadyMs) / 60000 - etaMinutes);
  if (!Number.isFinite(freeMinutes) || freeMinutes <= 0) return null;

  return {
    freeMinutes,
    approxPickupMs: target.ms,
    prevReadyMs,
    etaMinutes,
    approxPickupTime: target.raw,
    prevReadyTime: readyTrip.is_break
      ? readyTrip.break_end || readyTrip.appointment_time || null
      : readyTrip.available_at ||
        readyTrip.trip_details?.approximate_dropoff_time ||
        null,
  };
}

/**
 * @param {object} trip
 * @param {object[]|null|undefined} driverTrips
 * @param {object|null|undefined} multiloadRoutes
 */
export function calculateTripFreeTimeInBucket(
  trip,
  driverTrips,
  multiloadRoutes = null
) {
  const previousTrip = findPreviousTripInBucket(driverTrips, trip);
  return calculateTripFreeTime(
    trip,
    previousTrip,
    driverTrips,
    multiloadRoutes
  );
}

/**
 * Free time for a multiload group (always anchors on P1).
 * @param {object|null|undefined} p1Trip
 * @param {object[]|null|undefined} driverTrips
 * @param {object|null|undefined} multiloadRoutes
 */
export function calculateMultiloadGroupFreeTimeInBucket(
  p1Trip,
  driverTrips,
  multiloadRoutes = null
) {
  if (!p1Trip) return null;
  const anchored = {
    ...p1Trip,
    is_multiload: true,
    multiload_pickup_order: 1,
    is_multiload_primary: true,
  };
  return calculateTripFreeTimeInBucket(
    anchored,
    driverTrips,
    multiloadRoutes
  );
}

/**
 * Human label: "28 min" or "2h 7m"
 * @param {number} freeMinutes
 * @returns {string}
 */
export function formatFreeTimeMinutes(freeMinutes) {
  const mins = Math.max(0, Math.round(Number(freeMinutes) || 0));
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/**
 * Format HH:mm in company timezone for tooltip / compact display.
 * @param {string|Date|null|undefined} iso
 * @param {string} timezone
 * @returns {string}
 */
export function formatFreeTimeClock(iso, timezone = 'America/Los_Angeles') {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone,
    });
  } catch {
    return String(iso);
  }
}
